import React, { ComponentType, ReactNode, Suspense } from 'react'
import { View } from 'react-native'

/**
 * A registry entry:
 * - If the key matches the export name in the module, this is just the module loader func,
 *   usually `() => import('./module')`.
 * - To specify a key alias or name of the export, use `[module loader, name of the export in the module]`,
 *   e.g. [() => import('./module'), 'ExportName']
 */
export type LazyAssetLoader = (() => Promise<unknown>) | [() => Promise<unknown>, string]

export type LazyAssetLoaders<K extends string> = Record<K, LazyAssetLoader>

/**
 * Props a resolved lazy asset accepts: a Suspense `fallback`, the common sizing
 * props, plus the long tail of asset-specific props (SVG props, `backgroundFill`,
 * etc.) that vary per asset and aren't known statically.
 */
export type LazyAssetProps = {
  fallback?: ReactNode
  size?: number | string
  width?: number | string
  height?: number | string
  testID?: string
  [prop: string]: any
}

export type LazyAssetComponent = ComponentType<LazyAssetProps>

/**
 * Renders first available fallback while the chunk loads:
 * - Custom fallback prop
 * - A placeholder View placeholder to avoid layout shift, if size or width, height props provided
 * - null
 */
const FallbackComponent: LazyAssetComponent = ({ fallback, size, width, height }) => {
  if (fallback) {
    return <>{fallback}</>
  }
  const resolvedWidth = width ?? size
  const resolvedHeight = height ?? size
  if (resolvedWidth != null && resolvedHeight != null) {
    return <View style={{ width: resolvedWidth, height: resolvedHeight }} />
  }
  return null
}

export interface LazyAssetGetter<K extends string> {
  (name: K): LazyAssetComponent
  (name: string): LazyAssetComponent
  /**
   * Whether `name` is a registered asset key. Lets callers that accept arbitrary
   * strings decide whether to resolve it here.
   */
  has: (name: string) => boolean
}

/**
 * Builds a string-keyed component resolver for config-driven icon/illustration
 * lookups. Unlike `import * as Illustrations` (which forces every asset into the
 * importing bundle), each asset stays in its own lazy chunk and is fetched on
 * first render. While the chunk loads, a placeholder sized from the asset's
 * width/height/size props is rendered unless the caller passes an explicit `fallback`.
 */
export function createLazyAssetGetter<K extends string>(
  loaderName: string,
  loaders: LazyAssetLoaders<K>,
): LazyAssetGetter<K> {
  const cache: Record<string, LazyAssetComponent> = {}

  const getLazyAsset = (name: string): LazyAssetComponent => {
    if (!name || !Object.prototype.hasOwnProperty.call(loaders, name)) {
      if (__DEV__) {
        throw new Error(`LazyAssetRegistry: unknown asset name "${name}" in ${loaderName}`)
      }
      return FallbackComponent
    }
    if (!cache[name]) {
      const entry = loaders[name as K]
      const [load, exportName] = Array.isArray(entry) ? entry : [entry, name]
      const LazyComponent = React.lazy<ComponentType<any>>(() =>
        load().then((module) => {
          const resolved = (module as Record<string, ComponentType<any>>)[exportName]
          if (!resolved) {
            if (__DEV__) {
              throw new Error(`LazyAssetRegistry: ${loaderName}: asset "${name}" has no export "${exportName}"`)
            }
            return { default: FallbackComponent }
          }
          return { default: resolved }
        }),
      )
      const LazyWithSuspense: LazyAssetComponent = (props) => {
        const { fallback: _, ...rest } = props
        return (
          <Suspense fallback={<FallbackComponent {...props} />}>
            <LazyComponent {...rest} />
          </Suspense>
        )
      }
      LazyWithSuspense.displayName = `Lazy(${name})`
      cache[name] = LazyWithSuspense
    }
    return cache[name]
  }
  const getter = getLazyAsset as LazyAssetGetter<K>
  getter.has = (name: string): boolean => !!name && Object.prototype.hasOwnProperty.call(loaders, name)
  return getter
}
